import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Card,
  Container,
  ListGroup,
  Spinner,
} from 'react-bootstrap';
import { Link, useLocation, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import TextContentSep from '../components/common/TextContentSep';
import PublicShareHero from '../components/samples/PublicShareHero';
import { SITE_NAME } from '../constants/siteSeo';
import { sampleService } from '../services/sample.service';
import { joinLabeledValue } from '../utils/joinLabeledValue';
import { buildPublicLocationShareGroups } from '../utils/publicLocationShareGroups';
import { formatSampleTypeDisplay } from '../utils/sampleTypeLabels';
import {
  formatEntityDisplayName,
  softBreakIdentifier,
} from '../utils/softBreakIdentifier';
import { sampleRemainingQuantityBadgeBg } from '../utils/sampleUsageRecords';
import { formatSampleQuantityWithUnit } from '../utils/sampleUnitLabels';
import {
  buildPublicLocationSharePath,
  buildPublicSampleEncryptedPath,
} from '../utils/publicShareUrl';

export default function PublicLocationShare() {
  const { t } = useTranslation();
  const { token } = useParams<{ token: string }>();
  const location = useLocation();
  const [copied, setCopied] = useState(false);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['samples', 'location-share', token],
    queryFn: () => sampleService.getByLocationShareToken(token!),
    enabled: !!token,
  });

  const payload = data?.data;
  const publicUrl =
    typeof window !== 'undefined' && token
      ? `${window.location.origin}${buildPublicLocationSharePath(token)}`
      : '';

  const groups = useMemo(() => {
    if (!payload) return [];
    return buildPublicLocationShareGroups(payload.samples, payload.scope, {
      unassignedDrawer: t('sample.case_manager.unassigned_drawer'),
      unassignedCase: t('sample.case_manager.unassigned_case'),
    });
  }, [payload, t]);

  useEffect(() => {
    if (!payload) return;
    const prev = document.title;
    const parts = [
      formatEntityDisplayName(payload.storageRoom.name, t),
      payload.scope !== 'room'
        ? payload.drawerNumber != null
          ? `#${payload.drawerNumber}`
          : null
        : null,
      payload.scope === 'case' && payload.caseName
        ? formatEntityDisplayName(payload.caseName, t)
        : null,
    ].filter(Boolean);
    const place =
      parts.join(' · ') ||
      t('sample.location_share_public_title_short', { defaultValue: '公開サンプル一覧' });
    document.title = `${place} · ${t('sample.location_share_public_title_short', {
      defaultValue: '公開サンプル一覧',
    })} · ${SITE_NAME}`;
    return () => {
      document.title = prev;
    };
  }, [payload, t]);

  const copyUrl = async () => {
    if (!publicUrl) return;
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  if (isLoading) {
    return (
      <Container className="mt-5 text-center py-5" id="public-location-share-page">
        <Spinner animation="border" role="status" className="mb-3">
          <span className="visually-hidden">{t('common.loading')}</span>
        </Spinner>
        <p className="text-muted mb-0">
          {t('sample.location_share_loading', {
            defaultValue: '共有一覧を読み込んでいます…',
          })}
        </p>
      </Container>
    );
  }

  if (isError || !payload) {
    const status = (error as { response?: { status?: number } } | null)?.response?.status;
    return (
      <Container className="mt-4" id="public-location-share-page">
        <Alert variant="danger">
          <Alert.Heading>
            {status === 404
              ? t('sample.location_share_not_found', { defaultValue: '共有場所が見つかりません' })
              : t('sample.error_occurred', { defaultValue: 'An error occurred' })}
          </Alert.Heading>
          <p className="mb-3">
            {t('sample.location_share_load_failed', {
              defaultValue:
                '共有リンクが無効か、途中で切れている可能性があります。送り主にURLの再送を依頼してください。',
            })}
          </p>
          <Link to="/" className="btn btn-outline-secondary btn-sm">
            {t('common.back_to_home', { defaultValue: 'ホームへ' })}
          </Link>
        </Alert>
      </Container>
    );
  }

  const scopeLabel =
    payload.scope === 'room'
      ? t('sample.location_share_scope_room', { defaultValue: '保管庫' })
      : payload.scope === 'drawer'
        ? t('sample.location_share_scope_drawer', { defaultValue: '引き出し' })
        : t('sample.location_share_scope_case', { defaultValue: 'ケース' });

  const breadcrumbParts = [
    softBreakIdentifier(payload.storageRoom.name, t),
    payload.scope !== 'room'
      ? payload.drawerNumber != null
        ? `#${payload.drawerNumber}`
        : t('sample.case_manager.unassigned_drawer')
      : null,
    payload.scope === 'case'
      ? payload.caseName
        ? softBreakIdentifier(payload.caseName, t)
        : t('sample.case_manager.unassigned_case')
      : null,
  ].filter(Boolean);

  return (
    <div className="bg-light min-vh-100" id="public-location-share-page">
      <PublicShareHero
        iconClassName="fa-share-alt"
        title={t('sample.location_share_public_title_short', {
          defaultValue: '公開サンプル一覧',
        })}
        description={t('sample.location_share_public_desc', {
          defaultValue:
            '共有された場所のサンプルを確認できます。詳細・使用記録は各行から開けます。',
        })}
        copied={copied}
        onCopy={() => void copyUrl()}
        heroClassName="public-share-hero"
        copyButtonClassName="public-share-copy-btn"
        guestBadgeClassName="public-share-guest-badge"
        descriptionClassName="mb-0 opacity-75 small public-share-hero-desc d-none d-md-block"
      />

      <Container className="pb-4 px-3">
        <Card className="shadow-sm mb-3 border-0">
          <Card.Body className="py-2 px-3">
            <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
              <Badge bg="light" text="dark" className="border">
                {scopeLabel}
              </Badge>
              <TextContentSep />
              <span className="text-muted small">
                {t('sample.case_manager.sample_count', { count: payload.sampleCount })}
              </span>
            </div>
            <h2 className="h6 mb-0 text-break">{breadcrumbParts.join(' · ')}</h2>
            {payload.storageRoom.location ? (
              <p className="text-muted small mb-0 mt-1">
                <i className="fas fa-map-marker-alt me-1" aria-hidden="true" />
                {payload.storageRoom.location}
              </p>
            ) : null}
          </Card.Body>
        </Card>

        {payload.samples.length === 0 ? (
          <Alert variant="secondary" className="mb-0">
            {t('sample.location_share_empty', {
              defaultValue: 'この場所に表示できるサンプルはありません。',
            })}
          </Alert>
        ) : (
          groups.map((group) => (
            <Card key={group.key} className="shadow-sm mb-2 border-0">
              {group.label ? (
                <Card.Header className="py-1 px-3 small fw-semibold bg-white">
                  <i className="fas fa-box me-2 text-muted" aria-hidden="true" />
                  {softBreakIdentifier(group.label, t)}
                </Card.Header>
              ) : null}
              <ListGroup variant="flush">
                {group.samples.map((sample) => {
                  const disposed = !!sample.disposalDate;
                  const remaining = sample.remainingQuantity ?? 0;
                  const shareBlocked = !sample.encryptedId || sample.publicShareDisabled;
                  const meta = [
                    formatSampleTypeDisplay(sample.sampleType, t),
                    sample.bslLevel,
                    sample.creatorDisplayName,
                  ]
                    .filter(Boolean)
                    .join(' · ');
                  const openLabel = t('sample.location_share_open_sample', {
                    defaultValue: '詳細',
                  });
                  const blockedLabel = t('sample.location_share_sample_link_disabled', {
                    defaultValue: '共有停止中',
                  });
                  const displayName = formatEntityDisplayName(sample.name, t);
                  const href = sample.encryptedId
                    ? buildPublicSampleEncryptedPath(sample.encryptedId)
                    : '';
                  const qtyLabel = disposed
                    ? t('sample.status_disposed', { defaultValue: '廃棄' })
                    : joinLabeledValue(
                        t('sample.remaining_quantity_short', { defaultValue: '残' }),
                        formatSampleQuantityWithUnit(remaining, sample.unit, t)
                      );
                  const rowBody = (
                    <>
                      <div className="public-share-sample-main min-w-0">
                        <div className="fw-semibold text-truncate">
                          {softBreakIdentifier(sample.name, t)}
                        </div>
                        {meta ? (
                          <>
                            <TextContentSep />
                            <div className="small text-muted text-truncate" title={meta}>
                              {meta}
                            </div>
                          </>
                        ) : null}
                        <TextContentSep />
                      </div>
                      <Badge
                        bg={sampleRemainingQuantityBadgeBg(sample)}
                        className="public-share-qty-badge"
                        title={
                          disposed
                            ? qtyLabel
                            : t('sample.remaining_quantity')
                        }
                      >
                        {qtyLabel}
                      </Badge>
                      {shareBlocked ? (
                        <Badge
                          bg="secondary"
                          className="public-share-detail-chevron"
                          title={blockedLabel}
                        >
                          {blockedLabel}
                        </Badge>
                      ) : (
                        <span className="public-share-detail-chevron text-primary" aria-hidden="true">
                          <i className="fas fa-chevron-right" />
                        </span>
                      )}
                    </>
                  );
                  if (shareBlocked) {
                    return (
                      <ListGroup.Item
                        key={sample.id}
                        className="public-share-sample-row public-share-sample-row--blocked"
                        aria-label={`${blockedLabel} · ${displayName}`}
                        title={blockedLabel}
                      >
                        {rowBody}
                      </ListGroup.Item>
                    );
                  }
                  return (
                    <ListGroup.Item
                      key={sample.id}
                      action
                      as={Link}
                      to={href}
                      state={{ fromLocationShare: location.pathname }}
                      className="public-share-sample-row"
                      aria-label={`${openLabel} · ${displayName}`}
                    >
                      {rowBody}
                    </ListGroup.Item>
                  );
                })}
              </ListGroup>
            </Card>
          ))
        )}
      </Container>
    </div>
  );
}
